import React, { createContext, useContext, useState, useRef, useEffect, useCallback } from 'react';
import {
  checkModelExists, getModelFromStorage, loadAndStoreModel,
  cancelModelDownload, isModelDownloadInProgress,
  checkFundusModelExists, getFundusModelFromStorage, loadAndStoreFundusModel,
  checkEyesModelExists, getEyesModelFromStorage, loadAndStoreEyesModel,
  syncCachedModelsWithServerVersion, tryPersistModelBundleVersionWhenComplete,
  getTrainFeaturesMeanFromStorage, getTrainFeaturesCovinvFromStorage,
} from '../services/modelService';
import * as Sentry from '@sentry/react';
import { isMobileBrowser } from '../utils/deviceDetection';
import { getOnnxWorkerClient, resetOnnxWorkerClient } from '../workers/onnxWorkerClient';
import { terminateOpenCVWorker } from '../services/opencvWorkerManager';

/**
 * Sentinel placed in session/fundusSession/eyesSession when the model is
 * loaded inside the worker. Keeps all truthy checks in consumers working
 * without exposing a live InferenceSession on the main thread.
 */
const WORKER_SENTINEL = Object.freeze({ _workerOwned: true });

const MIN_MODEL_SIZE = 1024 * 1024; // 1 MB

/** ORT WASM backend failed to get memory (iOS Safari under pressure) — the only retryable load error. */
const isOomError = (err) =>
  /no available backend|out of memory/i.test(String(err?.message || err || ''));

/**
 * Last resort when even a clean retry can't get WASM memory: a fresh browser process is
 * the only remaining source of contiguous memory. Screening drafts are already persisted
 * to IndexedDB, so nothing is lost. Plain DOM (not React state) so it works no matter
 * which component triggered the load and can't be unmounted before the reload.
 */
function showLowMemoryRestartOverlayAndReload() {
  try {
    const el = document.createElement('div');
    el.setAttribute('style',
      'position:fixed;inset:0;z-index:99999;background:rgba(255,255,255,0.96);' +
      'display:flex;align-items:center;justify-content:center;text-align:center;' +
      'font-family:sans-serif;font-size:1.05rem;color:#333;padding:24px;');
    el.textContent = 'Device memory is low — restarting the app. Your data is saved.';
    document.body.appendChild(el);
  } catch (_) { /* overlay is cosmetic — reload regardless */ }
  setTimeout(() => window.location.reload(), 2500);
}

const ModelContext = createContext(null);

export const useModel = () => {
  const ctx = useContext(ModelContext);
  if (!ctx) throw new Error('useModel must be used within a ModelProvider');
  return ctx;
};

export const ModelProvider = ({ children }) => {
  // --- Public reactive state (same API as before) ---
  const [session,       setSession]       = useState(null);
  const [fundusSession, setFundusSession] = useState(null);
  const [eyesSession,   setEyesSession]   = useState(null);
  const [isLoading,      setIsLoading]      = useState(false);
  const [isFundusLoading, setIsFundusLoading] = useState(false);
  const [isEyesLoading,   setIsEyesLoading]   = useState(false);
  const [error, setError] = useState(null);
  const [isModelReady,  setIsModelReady]  = useState(false);
  const [isFundusReady, setIsFundusReady] = useState(false);
  const [isEyesReady,   setIsEyesReady]   = useState(false);

  // Refs mirror state for race-condition-free access inside async callbacks
  const sessionRef      = useRef(null);
  const fundusSessionRef = useRef(null);
  const eyesSessionRef   = useRef(null);
  const loadingRef      = useRef(false);
  const fundusLoadingRef = useRef(false);
  const eyesLoadingRef   = useRef(false);

  useEffect(() => { sessionRef.current      = session;       }, [session]);
  useEffect(() => { fundusSessionRef.current = fundusSession; }, [fundusSession]);
  useEffect(() => { eyesSessionRef.current   = eyesSession;   }, [eyesSession]);

  // Stable proxy that always delegates to the CURRENT singleton.
  // Safe across resetOnnxWorkerClient() calls because it reads the singleton lazily
  // at the time of each method call, not at provider mount time.
  const onnxClient = useRef({
    ping:           (...a) => getOnnxWorkerClient().ping(...a),
    loadModel:      (...a) => getOnnxWorkerClient().loadModel(...a),
    loadMahalFiles: (...a) => getOnnxWorkerClient().loadMahalFiles(...a),
    cacheTensor:    (...a) => getOnnxWorkerClient().cacheTensor(...a),
    runModel:       (...a) => getOnnxWorkerClient().runModel(...a),
    clearTensor:    (...a) => getOnnxWorkerClient().clearTensor(...a),
    disposeModel:   (...a) => getOnnxWorkerClient().disposeModel(...a),
    terminate:      (...a) => getOnnxWorkerClient().terminate(...a),
  }).current;

  // Cancel any in-flight download when provider unmounts
  useEffect(() => () => { if (loadingRef.current) cancelModelDownload(); }, []);

  // ── Helpers ─────────────────────────────────────────────────────────────

  function validateBuffer(buf, label) {
    if (!buf) throw new Error(`${label}: model data is null — re-download required.`);
    if (!(buf instanceof ArrayBuffer) && !(buf instanceof Uint8Array))
      throw new Error(`${label}: unexpected data type ${buf?.constructor?.name}.`);
    if (buf.byteLength < MIN_MODEL_SIZE)
      throw new Error(`${label}: file too small (${(buf.byteLength / 1024 / 1024).toFixed(2)} MB) — re-download required.`);
  }

  /**
   * Load a model into the worker, retrying once on init-OOM.
   *
   * On iOS Safari the ORT WASM backend can fail to allocate its (contiguous) memory
   * while transient decode buffers are still resident ("no available backend found.
   * ERR: [wasm] RangeError: Out of memory"). That pressure passes within ~a second, so:
   * free both workers (ONNX is half-initialized and broken anyway; OpenCV's own heap is
   * a fixed 48 MB reservation, released here so its headroom is available for the retry;
   * it re-spawns lazily on the next image), give Safari a beat to reclaim, then retry once
   * on a fresh worker. If the retry also fails, the device truly has no memory — restart
   * the app (drafts are in IndexedDB).
   *
   * Re-slices from modelData on retry because postMessage detaches the first buffer.
   */
  async function loadModelBufferWithOomRetry(kind, modelData) {
    const slice = () => modelData instanceof ArrayBuffer
      ? modelData.slice()
      : modelData.buffer.slice(modelData.byteOffset, modelData.byteOffset + modelData.byteLength);
    try {
      return await onnxClient.loadModel(kind, slice(), isMobileBrowser());
    } catch (err) {
      if (!isOomError(err)) throw err;
      resetOnnxClient();
      terminateOpenCVWorker();
      await new Promise(r => setTimeout(r, 1200));
      try {
        const result = await onnxClient.loadModel(kind, slice(), isMobileBrowser());
        Sentry.captureMessage('ONNX init-OOM recovered by worker-reset retry', {
          level: 'warning', tags: { errorType: 'onnx_oom_retry', kind },
        });
        return result;
      } catch (err2) {
        if (isOomError(err2)) {
          Sentry.captureMessage('ONNX init-OOM retry failed — restarting app', {
            level: 'error', tags: { errorType: 'onnx_oom_reload', kind },
          });
          showLowMemoryRestartOverlayAndReload();
          // Friendly message in case any dialog flashes before the reload lands.
          throw new Error('Device memory is low — restarting the app. Your data is saved.');
        }
        throw err2;
      }
    }
  }

  /** Try to load Mahalanobis files from OPFS and send them to the worker. Best-effort. */
  async function loadMahalFilesIntoWorker() {
    try {
      const [meanBuf, covinvBuf] = await Promise.all([
        getTrainFeaturesMeanFromStorage(),
        getTrainFeaturesCovinvFromStorage(),
      ]);
      if (meanBuf && covinvBuf) {
        // slice() ensures we own the buffer before transferring
        await onnxClient.loadMahalFiles(
          meanBuf   instanceof ArrayBuffer ? meanBuf.slice()   : meanBuf.buffer.slice(meanBuf.byteOffset, meanBuf.byteOffset + meanBuf.byteLength),
          covinvBuf instanceof ArrayBuffer ? covinvBuf.slice() : covinvBuf.buffer.slice(covinvBuf.byteOffset, covinvBuf.byteOffset + covinvBuf.byteLength),
        );
      }
    } catch (_) {
      // Mahalanobis files are optional at load time — inference still works without them
    }
  }

  // ── loadModel (DRDME) ────────────────────────────────────────────────────

  const loadModel = useCallback(async () => {
    if (sessionRef.current) return sessionRef.current;

    // Dedup concurrent calls
    if (loadingRef.current || isModelDownloadInProgress()) {
      let waited = 0;
      while ((isModelDownloadInProgress() || loadingRef.current) && waited < 60000) {
        await new Promise(r => setTimeout(r, 500));
        waited += 500;
        if (sessionRef.current) return sessionRef.current;
      }
      if (sessionRef.current) return sessionRef.current;
    }

    const token = localStorage.getItem('optha-token');
    if (!token) {
      setError('No auth token found. Please log in.');
      return null;
    }

    try {
      loadingRef.current = true;
      setIsLoading(true);
      setError(null);

      await syncCachedModelsWithServerVersion();
      let modelData = await (checkModelExists().then(ok => ok ? getModelFromStorage() : loadAndStoreModel()));
      if (!modelData) modelData = await getModelFromStorage();
      validateBuffer(modelData, 'DRDME model');

      await loadModelBufferWithOomRetry('drdme', modelData);

      // Send Mahalanobis files to worker alongside the main model
      await loadMahalFilesIntoWorker();

      setSession(WORKER_SENTINEL);
      sessionRef.current = WORKER_SENTINEL;
      setIsModelReady(true);
      setIsLoading(false);
      loadingRef.current = false;
      tryPersistModelBundleVersionWhenComplete();
      return WORKER_SENTINEL;
    } catch (err) {
      setError(err.message);
      Sentry.captureException(err, {
        tags: { errorType: 'model_loading_error', errorStage: 'model_context' },
        extra: { errorMessage: err.message, errorName: err.name },
      });
      throw err;
    } finally {
      setIsLoading(false);
      loadingRef.current = false;
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── setModelSession (splash compat — kept as no-op; splash calls loadModel directly) ──
  const setModelSession = useCallback(() => {
    // Sessions now live in the worker; this shim is kept so splash destructuring doesn't break.
  }, []);

  // ── loadFundusModel ──────────────────────────────────────────────────────

  const loadFundusModel = useCallback(async () => {
    if (fundusSessionRef.current) return fundusSessionRef.current;

    if (fundusLoadingRef.current) {
      let waited = 0;
      while (fundusLoadingRef.current && waited < 60000) {
        await new Promise(r => setTimeout(r, 500));
        waited += 500;
        if (fundusSessionRef.current) return fundusSessionRef.current;
      }
      if (fundusSessionRef.current) return fundusSessionRef.current;
    }

    const token = localStorage.getItem('optha-token');
    if (!token) { setError('No auth token found. Please log in.'); return null; }

    try {
      fundusLoadingRef.current = true;
      setIsFundusLoading(true);
      setError(null);

      await syncCachedModelsWithServerVersion();
      let modelData = await (checkFundusModelExists().then(ok => ok ? getFundusModelFromStorage() : loadAndStoreFundusModel()));
      if (!modelData) modelData = await getFundusModelFromStorage();
      validateBuffer(modelData, 'Fundus model');

      await loadModelBufferWithOomRetry('fundus', modelData);

      setFundusSession(WORKER_SENTINEL);
      fundusSessionRef.current = WORKER_SENTINEL;
      setIsFundusReady(true);
      setIsFundusLoading(false);
      fundusLoadingRef.current = false;
      tryPersistModelBundleVersionWhenComplete();
      return WORKER_SENTINEL;
    } catch (err) {
      setError(err.message);
      Sentry.captureException(err, {
        tags: { errorType: 'fundus_model_loading_error', errorStage: 'model_context' },
        extra: { errorMessage: err.message, errorName: err.name },
      });
      throw err;
    } finally {
      setIsFundusLoading(false);
      fundusLoadingRef.current = false;
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── loadEyesModel ────────────────────────────────────────────────────────

  const loadEyesModel = useCallback(async () => {
    if (eyesSessionRef.current) return eyesSessionRef.current;

    if (eyesLoadingRef.current) {
      let waited = 0;
      while (eyesLoadingRef.current && waited < 60000) {
        await new Promise(r => setTimeout(r, 500));
        waited += 500;
        if (eyesSessionRef.current) return eyesSessionRef.current;
      }
      if (eyesSessionRef.current) return eyesSessionRef.current;
    }

    const token = localStorage.getItem('optha-token');
    if (!token) { setError('No auth token found. Please log in.'); return null; }

    try {
      eyesLoadingRef.current = true;
      setIsEyesLoading(true);
      setError(null);

      await syncCachedModelsWithServerVersion();
      let modelData = await (checkEyesModelExists().then(ok => ok ? getEyesModelFromStorage() : loadAndStoreEyesModel()));
      if (!modelData) modelData = await getEyesModelFromStorage();
      validateBuffer(modelData, 'Eyes model');

      await loadModelBufferWithOomRetry('eyes', modelData);

      setEyesSession(WORKER_SENTINEL);
      eyesSessionRef.current = WORKER_SENTINEL;
      setIsEyesReady(true);
      setIsEyesLoading(false);
      eyesLoadingRef.current = false;
      tryPersistModelBundleVersionWhenComplete();
      return WORKER_SENTINEL;
    } catch (err) {
      setError(err.message);
      Sentry.captureException(err, {
        tags: { errorType: 'eyes_model_loading_error', errorStage: 'model_context' },
        extra: { errorMessage: err.message, errorName: err.name },
      });
      throw err;
    } finally {
      setIsEyesLoading(false);
      eyesLoadingRef.current = false;
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Release helpers (free WASM in worker + clear ready state) ────────────

  const releaseSession = useCallback(() => {
    if (sessionRef.current) {
      sessionRef.current = null;
      onnxClient.disposeModel('drdme').catch(() => {});
    }
    setSession(null);
    setIsModelReady(false);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const releaseFundusSession = useCallback(() => {
    if (fundusSessionRef.current) {
      fundusSessionRef.current = null;
      onnxClient.disposeModel('fundus').catch(() => {});
    }
    setFundusSession(null);
    setIsFundusReady(false);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const releaseEyesSession = useCallback(() => {
    if (eyesSessionRef.current) {
      eyesSessionRef.current = null;
      onnxClient.disposeModel('eyes').catch(() => {});
    }
    setEyesSession(null);
    setIsEyesReady(false);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Reset the ONNX worker and clear all session state.
  // Must be called instead of resetOnnxWorkerClient() directly so ModelContext
  // session refs/state stays in sync with the new empty worker.
  const resetOnnxClient = useCallback(() => {
    resetOnnxWorkerClient();
    sessionRef.current = null;      setSession(null);      setIsModelReady(false);
    fundusSessionRef.current = null; setFundusSession(null); setIsFundusReady(false);
    eyesSessionRef.current = null;   setEyesSession(null);   setIsEyesReady(false);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <ModelContext.Provider value={{
      session,
      fundusSession,
      eyesSession,
      isLoading,
      isFundusLoading,
      isEyesLoading,
      error,
      isModelReady,
      isFundusReady,
      isEyesReady,
      loadModel,
      loadFundusModel,
      loadEyesModel,
      releaseSession,
      releaseFundusSession,
      releaseEyesSession,
      setModelSession,
      onnxClient,
      resetOnnxClient,
    }}>
      {children}
    </ModelContext.Provider>
  );
};

export default ModelContext;
